// SPDX-License-Identifier: BUSL-1.1

/**
 * Agent liveness derivation (HB-02, NocoDB #4403).
 *
 * Pure function that reduces (status, lastHeartbeat) into a 5-state health
 * label for dashboard rendering. The UI calls this at render time so
 * thresholds can change without DB migrations.
 *
 * Reference: Multica derive-health.ts pattern; AgentCenter 2026 5-state
 * monitoring taxonomy (active|idle|stuck|sleeping|unresponsive).
 *
 * "Stuck" (alive but not progressing) requires evidence beyond timestamp
 * freshness — out of scope here; covered by HB-04 silent-run watchdog
 * once HB-03 activity events ship.
 */

/** Five states derivable from (status, lastHeartbeat) alone. */
export type AgentHealth =
  | 'active' // running AND last ping within ACTIVE_MAX_AGE_MS
  | 'idle' // idle AND last ping within IDLE_MAX_AGE_MS
  | 'recently_lost' // last ping within RECENTLY_LOST_MAX_AGE_MS but not active/idle
  | 'offline' // last ping older than recently_lost but newer than GC threshold
  | 'about_to_gc'; // last ping older than OFFLINE_MAX_AGE_MS, or never

export const AGENT_HEALTH_LABELS: Record<AgentHealth, string> = {
  active: 'Active',
  idle: 'Idle',
  recently_lost: 'Recently lost',
  offline: 'Offline',
  about_to_gc: 'About to GC',
};

/**
 * Tone hint for badge rendering. Mirrors the existing health-tone vocabulary
 * in lib/health.ts so consumers can share badge color tokens.
 */
export type AgentHealthTone = 'ok' | 'neutral' | 'warning' | 'error' | 'muted';

export const AGENT_HEALTH_TONE: Record<AgentHealth, AgentHealthTone> = {
  active: 'ok',
  idle: 'neutral',
  recently_lost: 'warning',
  offline: 'muted',
  about_to_gc: 'error',
};

// ---------------------------------------------------------------------------
// Thresholds — exported so HB-04 (silent-run watchdog) imports the same
// constants. Keep them in milliseconds for direct comparison with Date.now().
// ---------------------------------------------------------------------------

/** Recent enough to count as `active` for a `running` agent. */
export const ACTIVE_MAX_AGE_MS = 60_000; // 60 s

/** Recent enough to count as `idle` for an `idle` agent. */
export const IDLE_MAX_AGE_MS = 5 * 60_000; // 5 min

/** Older than this and we say `recently_lost` rather than active/idle. */
export const RECENTLY_LOST_MAX_AGE_MS = 5 * 60_000; // 5 min

/** Older than this and we GC the runtime row entirely. */
export const OFFLINE_MAX_AGE_MS = 6 * 24 * 3600_000; // 6 days

export interface DeriveAgentHealthInput {
  /** Canonical status from ops_agents.status. */
  status: string;
  /**
   * Last heartbeat timestamp. Accepts Date, ISO string, or null/undefined
   * for agents that have never reported.
   */
  lastHeartbeat: Date | string | null | undefined;
}

interface DeriveAgentHealthOptions {
  /** Override `Date.now()` for deterministic tests. */
  now?: number;
}

/**
 * Reduce agent state + last-heartbeat age into a 5-state liveness label.
 * Pure, deterministic, no side effects.
 */
export function deriveAgentHealth(
  input: DeriveAgentHealthInput,
  options: DeriveAgentHealthOptions = {},
): AgentHealth {
  const now = options.now ?? Date.now();

  if (input.lastHeartbeat == null) {
    return 'about_to_gc';
  }

  const lastMs =
    typeof input.lastHeartbeat === 'string'
      ? Date.parse(input.lastHeartbeat)
      : input.lastHeartbeat.getTime();

  if (Number.isNaN(lastMs)) {
    return 'about_to_gc';
  }

  const ageMs = now - lastMs;

  if (input.status === 'running' && ageMs < ACTIVE_MAX_AGE_MS) {
    return 'active';
  }

  if (input.status === 'idle' && ageMs < IDLE_MAX_AGE_MS) {
    return 'idle';
  }

  if (ageMs < RECENTLY_LOST_MAX_AGE_MS) {
    return 'recently_lost';
  }

  if (ageMs < OFFLINE_MAX_AGE_MS) {
    return 'offline';
  }

  return 'about_to_gc';
}
